import isNil from 'lodash/isNil'
import { InvoiceRecord } from '../../../finance/api/dto/invoice-list.dto'

export interface PeriodGroup {
  groupKey: string
  period: string
  latest: InvoiceRecord
  olderVersions: InvoiceRecord[]
}

export type PeriodTableRow = InvoiceRecord & {
  rowKind: 'period'
  olderVersions: InvoiceRecord[]
}

export type VersionTableRow = InvoiceRecord & {
  rowKind: 'version'
}

export type TableRow = PeriodTableRow | VersionTableRow

/**
 * Each email template is its own invoice for the month, so a January OE First
 * and OE Final are two groups with their own versions, not one superseding the
 * other.
 */
export const toGroupKey = (
  row: Pick<InvoiceRecord, 'period' | 'invoiceTemplate'>
): string => `${row.period}|${row.invoiceTemplate}`

/**
 * Group the flat invoice list by billing period and email template, first-seen
 * order preserved. `latest` is the `isLatest` row (highest `version` if the
 * flag is absent). `olderVersions` is newest-first so expand can list them
 * under the latest.
 */
export const groupByPeriod = (rows: InvoiceRecord[]): PeriodGroup[] => {
  const order: string[] = []
  const byGroup = new Map<string, InvoiceRecord[]>()
  for (const row of rows) {
    const groupKey = toGroupKey(row)
    const existing = byGroup.get(groupKey)
    if (existing) {
      existing.push(row)
    } else {
      byGroup.set(groupKey, [row])
      order.push(groupKey)
    }
  }

  return order.flatMap((groupKey) => {
    const versions = [...(byGroup.get(groupKey) ?? [])].sort(
      (a, b) => b.version - a.version
    )
    const latest = versions.find((row) => row.isLatest) ?? versions[0]
    if (isNil(latest)) return []
    return [
      {
        groupKey,
        period: latest.period,
        latest,
        olderVersions: versions.filter((row) => row.id !== latest.id),
      },
    ]
  })
}

export const flattenPeriodGroups = (
  groups: PeriodGroup[],
  expandedGroups: ReadonlySet<string>
): TableRow[] => {
  const rows: TableRow[] = []
  for (const group of groups) {
    rows.push({
      ...group.latest,
      rowKind: 'period',
      olderVersions: group.olderVersions,
    })
    if (!expandedGroups.has(group.groupKey)) continue
    for (const older of group.olderVersions) {
      rows.push({ ...older, rowKind: 'version' })
    }
  }
  return rows
}
